import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useModal } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';

import { Page, HomeValueAndPropertySearchWrapper } from './Homepage.styles';
import { AuthModalMode } from 'shared/components/AuthModal/AuthModal.types';

import {
    HomepageHead,
    HeroContainer,
    HowMuchIsMyHomeWorth,
    SearchSuggestionsTownsAndCities,
    PropertyNews
} from '../components';
import { Tab } from '../components/HeroContainer/HeroContainer.types';
import { ContentfulImageWrapper } from 'homepage/queries/heroContent.types';
import { ArticlesForHomepageQuery } from 'homepage/queries/propertyNews.generated';
import { HomepageRegistrationFooter } from 'homepage/components/HomepageRegistrationFooter/HomepageRegistrationFooter';
import usePageTracking from 'shared/hooks/usePageTracking';
import { TaxonomyPage, TaxonomySection } from 'shared/tracking/contracts';
import { AgentValuation } from './../components/AgentValuation/AgentValuation';

type HomepageProps = {
    brandHeadline: string;
    h1TitleTag: string;
    heroImage: ContentfulImageWrapper;
    propertyNewsArticles: ArticlesForHomepageQuery['articles'];
};

const DynamicAuthModal = dynamic(
    () => import('shared/components/AuthModal/AuthModal'),
    { ssr: false }
);

export const Homepage = (props: HomepageProps) => {
    const {
        brandHeadline,
        h1TitleTag,
        heroImage,
        propertyNewsArticles
    } = props;
    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();
    const [tabSelected, setTabSelected] = useState<Tab>('for-sale');
    const [authMode, setAuthMode] = useState<AuthModalMode>('sign-in');

    usePageTracking(TaxonomyPage.HOMEPAGE, {
        section: TaxonomySection.HOMEPAGE
    });

    const handleOpenAuthModal = (
        triggerOrigin: React.RefObject<HTMLElement> | null,
        mode: AuthModalMode
    ) => {
        setAuthMode(mode);
        openAuthModal(triggerOrigin);
    };

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely',
            eventAction: 'home_valuation_cta_homeworth_image_copy_hero_image',
            eventLabel: 'original'
        });
    }, []);

    return (
        <>
            <HomepageHead />
            <Page>
                <HeroContainer
                    tabSelected={tabSelected}
                    setTabSelected={setTabSelected}
                    brandHeadline={brandHeadline}
                    title={h1TitleTag}
                    image={heroImage}
                    mainContentId="main-content"
                />
                <HomeValueAndPropertySearchWrapper>
                    <HowMuchIsMyHomeWorth />
                </HomeValueAndPropertySearchWrapper>
                <AgentValuation />
                <SearchSuggestionsTownsAndCities />
                <PropertyNews articles={propertyNewsArticles} />
                <HomepageRegistrationFooter
                    openAuthModal={handleOpenAuthModal}
                />
            </Page>
            <DynamicAuthModal
                heading="Get more from Zoopla"
                initialMode={authMode}
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                actionAfterAuth={{
                    action: () => null,
                    eventLabel: 'homepage',
                    segment: {
                        page: TaxonomyPage.HOMEPAGE,
                        section: TaxonomySection.HOMEPAGE
                    }
                }}
            />
        </>
    );
};
